import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { showApiError, showSuccess } from "@/lib/toast";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addNewHospitalMenuCategoryItem } from "@/sideEffects/api";
import { FormSchemaType, HospitalityMenuCategoryItemMetaForm } from "./form/HospitalityMenuCategoryItemMetaForm";
import { HospitalityMenuCategory } from "@/lib/types";

interface Props {
  category: HospitalityMenuCategory;
}

export const AddNewHospitalityMenuCategoryItemDialog: React.FC<Props> = ({ category }) => {
  const { t } = useTranslation();
  const [open, onOpenChange] = useState(false);

  const queryClient = useQueryClient();
  const addNewHospitalMenuCategoryItemMutation = useMutation({
    mutationFn: (values: FormSchemaType) =>
      addNewHospitalMenuCategoryItem({
        name: values.name,
        description: values.description ?? "",
        price: values.price,
        hospitalityMenuId: category.hMenu_Id,
        hospitalityMenuCategoryId: category.id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getHospitalityMenuCategoryItem"],
      });
      showSuccess(t("common.addedSuccess"));
      onOpenChange(false);
    },
    onError: showApiError,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          {t("common.addNewEntity", { entity: t("hospitalityMenu.item") })}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.addNewEntity", { entity: t("hospitalityMenu.item") })}</DialogTitle>
          <DialogDescription>
            <HospitalityMenuCategoryItemMetaForm
              buttonText={t("common.add")}
              onSubmit={(values) => addNewHospitalMenuCategoryItemMutation.mutate(values)}
              isLoading={addNewHospitalMenuCategoryItemMutation.isPending}
            />
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
